import * as Inovx from '@shared';
import { ApiError, JsonValue, apiGet, apiPostForm, apiPostJson, displayApiError } from '@shared/api/apiClient';

var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var SystemActions = Inovx.Shared.System.Actions;
var url = `${UrlUtilities.getApiUrl()}/FacilityApi/api/Asset`;

interface IAssetIdentityApiData extends IVersionedIdentityItem {
    Identity: string;
    FacilityID: string;
    AssetClassID: string;
    Source: AssetSourceType;
}

interface INativeAssetApiData extends IVersionedIdentityItem {
    AssetID: string;
    Source: AssetSourceType;
    ApplicableOntologyIDs?: string[];
}

interface IAssetTagApiData extends IVersionedIdentityItem {
    Caption?: string | null;
    AssetID?: string;
    View360ID?: string;
    Target2D?: IVector2D;
    Target3D?: IVector3D;
    TargetBoxMin?: IVector3D;
    TargetBoxMax?: IVector3D;
    CameraPosition?: IVector3D;
    ApplicableOntologyIDs?: string[];
}

interface IAssetTagUpdateApiData extends IAssetTagApiData {
    AssetClassID: string;
    FacilityID?: string;
    AssetIdentity?: string;
}

interface IAssetTagUpdateApiResponse {
    Asset: IAssetIdentityApiData;
    NativeAsset: INativeAssetApiData;
    Tag: IAssetTagApiData;
    DeletedAssetID: string;
}

const createAssetTagApiFormData = (tag: IAssetTagUpdateApiData) => {
    const form_data = new FormData();

    if (tag.ID) form_data.append("ID", tag.ID);
    if (tag.VersionToken) form_data.append("VersionToken", tag.VersionToken);
    if (tag.AssetID) form_data.append("AssetID", tag.AssetID);

    form_data.append("FacilityID", tag.FacilityID);
    form_data.append("AssetClassID", tag.AssetClassID);
    form_data.append("AssetIdentity", tag.AssetIdentity);
    if (tag.View360ID) form_data.append("View360ID", tag.View360ID);

    if (tag.Target2D) {
        form_data.append("Target2D.X", tag['Target2D'].X.toString())
        form_data.append("Target2D.Y", tag['Target2D'].Y.toString())
    }
    if (tag.Target3D) {
        form_data.append("Target3D.X", tag['Target3D'].X.toString())
        form_data.append("Target3D.Y", tag['Target3D'].Y.toString())
        form_data.append("Target3D.Z", tag['Target3D'].Z.toString())
    }
    if (tag.TargetBoxMin) {
        form_data.append("TargetBoxMin.X", tag['TargetBoxMin'].X.toString())
        form_data.append("TargetBoxMin.Y", tag['TargetBoxMin'].Y.toString())
        form_data.append("TargetBoxMin.Z", tag['TargetBoxMin'].Z.toString())
    }
    if (tag.TargetBoxMax) {
        form_data.append("TargetBoxMax.X", tag['TargetBoxMax'].X.toString())
        form_data.append("TargetBoxMax.Y", tag['TargetBoxMax'].Y.toString())
        form_data.append("TargetBoxMax.Z", tag['TargetBoxMax'].Z.toString())
    }
    if (tag.CameraPosition) {
        form_data.append("CameraPosition.X", tag['CameraPosition'].X.toString())
        form_data.append("CameraPosition.Y", tag['CameraPosition'].Y.toString())
        form_data.append("CameraPosition.Z", tag['CameraPosition'].Z.toString())
    }
    return form_data;
}

const AssetServices = { ...Inovx.Shared.Flux.BaseService,

    createAssetTag: async function (tag: IAssetTagUpdateApiData) {
        const form_data = createAssetTagApiFormData(tag);
        try {
            return await apiPostForm<IAssetTagUpdateApiResponse>(`${url}/Tags/Create`, form_data, { responseType: 'json' });
        }
        catch (error) {
            this.handleUpdateWebApiError(error);
            throw error;
        }
    },

    editAssetTag: async function (tag: IAssetTagUpdateApiData, actionConfirmed: boolean) {
        const form_data = createAssetTagApiFormData(tag);
        form_data.append("ActionConfirmed", actionConfirmed.toString());
        try {
            return await apiPostForm<IAssetTagUpdateApiResponse>(`${url}/Tags/Update`, form_data, { responseType: 'json' });
        }
        catch (error) {
            this.handleUpdateWebApiError(error);
            throw error;
        }
    },

    handleUpdateWebApiError: function (error: ApiError) {
        if (error.responseData?.Reason == 'InvalidValue') {
            SystemActions.displayErrorMessage({
                message: InovxLoader.t("facility:message.webapierror"),
            });
        } else {
            displayApiError(error);
        }
    },

    deleteAssetTag: function (tag: IVersionedIdentityItem, deleteParentAsset: boolean) {
        return new Promise<void>((resolve, reject) => {
            this.post(`${url}/Tags/Delete`,
                { ID: tag.ID, VersionToken: tag.VersionToken, deleteParentAsset: deleteParentAsset },
                () => {
                    resolve();
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },

    getAssetTag: function (id: string) {
        return new Promise<IAssetTagApiData>((resolve, reject) => {
            this.get(`${url}/Tags/ById`,
                { id } ,
                (retData: IAssetTagApiData) => {
                    resolve(retData);
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },

    controller: new AbortController(),
    getAssetAttributes: function (id: string) {
        // Cancel previous request if it exists
        if (this.controller) {
            this.controller.abort();
            this.controller = new AbortController();
        }
        const { signal } = this.controller;
        return new Promise<IAttributeSetValue[]>((resolve, reject) => {
            apiGet<IAttributeSetValue[]>(`${url}/Attributes?id=${id}`, null, { signal: signal, responseType: 'json' })
                .then(data => {
                    resolve(data);
                })
                .catch(error => {
                    // apiClient normalizes native aborts to ApiError instances.
                    // Reject here so the action layer can treat cancellation as
                    // a normal, handled outcome instead of reporting it as an
                    // API failure or leaving this promise pending.
                    if (error?.isAbort === true || error?.name === 'AbortError') {
                        reject(error);
                        return;
                    }
                    this.handleWebApiError(error);
                    reject(error);
                });
        });
    },
    getExternalAssetAttributes: function (id: string, placeholderID: string, signal?: AbortSignal) {
        return apiGet<IAttributeSetValue[]>(
            `${url}/Attributes/External?id=${encodeURIComponent(id)}&placeholderId=${encodeURIComponent(placeholderID)}`,
            null,
            { signal, responseType: 'json' });
    },
    updateAssetAttribute: function (attribute: IAssetAttributeRequest) {
        return new Promise<void>((resolve, reject) => {
            this.post(`${url}/Attributes/SetValue`,
                attribute,
                () => {
                    resolve();
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },

    executeQuery: async function (ontologyId: string, queryRules: unknown): Promise<IAssetIdentity[]> {
        return await apiPostJson<IAssetIdentity[]>(`${url}/Query?ontologyId=${encodeURIComponent(ontologyId)}`, queryRules as JsonValue, { responseType: 'json' });
    },
};

export default AssetServices;
